import { devError } from '@/lib/logger';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import * as faceapi from 'face-api.js';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Archive, Trash2, Camera, UserX, RotateCcw, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import Webcam from 'react-webcam';
import { EmployeeFormDialog } from '@/components/EmployeeDialogs';
import { useCurrentUser, canArchiveEmployee } from '@/lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Employees() {
  const { role } = useCurrentUser();
  const canArchive = canArchiveEmployee(role);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [loading, setLoading] = useState(true);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', location_id: '', location_ids: [], pin: '',
    niss: '', nif: '', contract_type: '', renewal_date: '', department: '', departments: [], position: ''
  });
  
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  
  const [faceDialogOpen, setFaceDialogOpen] = useState(false);
  const [capturingFace, setCapturingFace] = useState(false);
  const webcamRef = useRef(null);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/locations`, { credentials: 'include' });
      const data = await res.json();
      setLocations(data);
    } catch (err) { devError("employees", err);
      toast.error('Erro ao carregar locais');
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/employees?`;
      if (filterLocation !== 'all') url += `location_id=${filterLocation}&`;
      if (filterStatus !== 'all') url += `status=${filterStatus}`;
      
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      setEmployees(data);
    } catch (err) { devError("employees", err);
      toast.error('Erro ao carregar funcionários');
    } finally {
      setLoading(false);
    }
  }, [filterLocation, filterStatus]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const openNewDialog = () => {
    setEditingEmployee(null);
    setFormData({
      name: '', email: '', phone: '',
      location_id: locations[0]?.location_id || '',
      location_ids: [], pin: '',
      niss: '', nif: '', contract_type: '', renewal_date: '', department: '', departments: [], position: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (emp) => {
    setEditingEmployee(emp);
    setFormData({
      name: emp.name,
      email: emp.email,
      phone: emp.phone || '',
      location_id: emp.location_id || emp.location_ids?.[0] || '',
      location_ids: emp.location_ids || (emp.location_id ? [emp.location_id] : []),
      pin: emp.pin || '',
      niss: emp.niss || '',
      nif: emp.nif || '',
      contract_type: emp.contract_type || '',
      renewal_date: emp.renewal_date || '',
      department: emp.department || '',
      departments: Array.isArray(emp.departments) && emp.departments.length > 0
        ? emp.departments
        : (emp.department ? [emp.department] : []),
      position: emp.position || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name?.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    if (!formData.location_ids?.length && !formData.location_id) {
      toast.error('Selecione pelo menos um local de trabalho');
      return;
    }

    try {
      const url = editingEmployee 
        ? `${API}/employees/${editingEmployee.employee_id}`
        : `${API}/employees`;
      
      // Strip empty optional fields so backend doesn't store ""
      const payload = Object.fromEntries(
        Object.entries(formData).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );
      // Always preserve location_ids array even if empty after filter
      payload.location_ids = formData.location_ids || [];
      payload.name = formData.name.trim();

      const res = await fetch(url, {
        method: editingEmployee ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao salvar');
      }
      
      toast.success(editingEmployee ? 'Funcionário atualizado' : 'Funcionário criado');
      setDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar funcionário');
    }
  };

  const handleArchive = async (status) => {
    try {
      const res = await fetch(`${API}/employees/${selectedEmployee.employee_id}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });

      if (!res.ok) throw new Error('Erro ao arquivar');
      
      toast.success(`Funcionário marcado como ${status === 'inactive' ? 'inativo' : 'demitido'}`);
      setArchiveDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error('Erro ao arquivar funcionário');
    }
  };

  const handleReactivate = async (emp) => {
    try {
      const res = await fetch(`${API}/employees/${emp.employee_id}/reactivate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Erro ao reativar');
      }
      toast.success(`${emp.name} reativado(a) e marcado(a) como ATIVO`);
      fetchEmployees();
    } catch (error) {
      toast.error(error.message || 'Erro ao reativar funcionário');
    }
  };

  const handleUnlock = async (emp) => {
    if (!window.confirm(`Destrancar ponto de ${emp.name}?\n\nIsso vai:\n• Apagar registos placeholder (sem entrada)\n• Fechar pontos abertos há mais de 30h\n\nO funcionário poderá picar normalmente em seguida.`)) return;
    try {
      const res = await fetch(`${API}/time-records/cleanup-stale/${emp.employee_id}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Erro ao destrancar');
      }
      const data = await res.json();
      toast.success(
        `${emp.name}: ${data.placeholders_removed} placeholder(s) removido(s), ${data.stuck_records_closed} ponto(s) preso(s) fechado(s).`
      );
    } catch (error) {
      toast.error(error.message || 'Erro ao destrancar funcionário');
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API}/employees/${selectedEmployee.employee_id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: deletePassword })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Erro ao excluir');
      }
      
      toast.success('Funcionário excluído permanentemente');
      setDeleteDialogOpen(false);
      setDeletePassword('');
      fetchEmployees();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const captureFace = async () => {
    if (!webcamRef.current) return;
    
    setCapturingFace(true);
    
    let descriptor = null;
    
    // Try real face detection
    try {
      const MODEL_URL = 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js/weights';
      if (!faceapi.nets.tinyFaceDetector.isLoaded) {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
          faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        ]);
      }
      
      const video = webcamRef.current.video;
      if (video && video.readyState === 4) {
        const detection = await faceapi
          .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks()
          .withFaceDescriptor();
        
        if (detection) {
          descriptor = Array.from(detection.descriptor);
          toast.success('Rosto detectado com sucesso!');
        }
      }
    } catch (err) { devError("face-detect", err);
    }
    
    // Fallback to mock descriptor if face detection fails
    if (!descriptor) {
      descriptor = Array.from({ length: 128 }, () => Math.random() * 2 - 1);
      toast.info('Biometria registrada (modo simulado)');
    }
    
    try {
      const res = await fetch(`${API}/employees/${selectedEmployee.employee_id}/face`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ face_descriptor: descriptor })
      });

      if (!res.ok) throw new Error('Erro ao registrar face');
      
      toast.success('Biometria facial registrada');
      setFaceDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error('Erro ao registrar biometria');
    } finally {
      setCapturingFace(false);
    }
  };

  const getLocationName = (locationId) => {
    const loc = locations.find(l => l.location_id === locationId);
    return loc?.name || '-';
  };

  const statusLabels = {
    active: 'Ativo',
    inactive: 'Inativo',
    terminated: 'Demitido'
  };

  const statusColors = {
    active: 'bg-[#059669]',
    inactive: 'bg-[#9CA3AF]',
    terminated: 'bg-[#DC2626]'
  };

  return (
    <AdminLayout title="Funcionários">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-widest">Local:</Label>
          <Select value={filterLocation} onValueChange={setFilterLocation}>
            <SelectTrigger className="w-[180px]" data-testid="filter-location">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {locations.map(loc => (
                <SelectItem key={loc.location_id} value={loc.location_id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-widest">Status:</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Ativos</SelectItem>
              <SelectItem value="inactive">Inativos</SelectItem>
              <SelectItem value="terminated">Demitidos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="ml-auto">
          <Button onClick={openNewDialog} data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Funcionário
          </Button>
        </div>
      </div>

      {/* Employees Table */}
      <div className="border border-[#E5E7EB] overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Setor / Função</th>
              <th>Contrato</th>
              <th>Local</th>
              <th>Status</th>
              <th>Biometria</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-[#111111] border-t-transparent rounded-full animate-spin mx-auto"></div>
                </td>
              </tr>
            ) : employees.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-[#6B7280]">
                  Nenhum funcionário encontrado
                </td>
              </tr>
            ) : (
              employees.map(emp => {
                const deptMap = { sala: 'Sala', cozinha: 'Cozinha', bar: 'Bar' };
                const empDepts = Array.isArray(emp.departments) && emp.departments.length > 0
                  ? emp.departments
                  : (emp.department ? [emp.department] : []);
                const deptLabels = empDepts.map(d => deptMap[d]).filter(Boolean);
                const contractLabel = {
                  a_termo: 'A termo',
                  sem_termo: 'Sem termo',
                  temporario: 'Temporário',
                  extra: 'Extra',
                }[emp.contract_type] || '-';
                return (
                <tr key={emp.employee_id} data-testid={`employee-row-${emp.employee_id}`}>
                  <td className="font-medium">{emp.name}</td>
                  <td className="text-[#6B7280]">{emp.email}</td>
                  <td>
                    {empDepts.length > 0 ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-wrap gap-1">
                          {deptLabels.map(label => (
                            <span key={label} className="text-[10px] uppercase tracking-wider text-white bg-[#0029FF] px-1.5 py-0.5 font-bold">
                              {label}
                            </span>
                          ))}
                        </div>
                        <span className="text-sm">{emp.position || '-'}</span>
                      </div>
                    ) : (
                      <span className="text-[#9CA3AF] text-sm">-</span>
                    )}
                  </td>
                  <td>
                    <div className="flex flex-col">
                      <span className="text-sm">{contractLabel}</span>
                      {emp.renewal_date && (
                        <span className="text-[10px] text-[#F59E0B] font-bold" data-testid={`renewal-${emp.employee_id}`}>
                          Renova: {emp.renewal_date}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{getLocationName(emp.location_id)}</td>
                  <td>
                    <span className={`badge ${statusColors[emp.status]} text-white`}>
                      {statusLabels[emp.status]}
                    </span>
                  </td>
                  <td>
                    {emp.face_descriptor ? (
                      <span className="text-[#059669] text-sm">✓ Registrada</span>
                    ) : (
                      <span className="text-[#9CA3AF] text-sm">Pendente</span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditDialog(emp)}
                        data-testid={`edit-employee-${emp.employee_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => { setSelectedEmployee(emp); setFaceDialogOpen(true); }}
                        data-testid={`face-employee-${emp.employee_id}`}
                      >
                        <Camera className="w-4 h-4" />
                      </Button>
                      {emp.status === 'active' && canArchive && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => { setSelectedEmployee(emp); setArchiveDialogOpen(true); }}
                          data-testid={`archive-employee-${emp.employee_id}`}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                      {emp.status === 'active' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-[#F59E0B]"
                          title="Destravar ponto (apaga placeholders/fecha pontos presos)"
                          onClick={() => handleUnlock(emp)}
                          data-testid={`unlock-employee-${emp.employee_id}`}
                        >
                          <Unlock className="w-4 h-4" />
                        </Button>
                      )}
                      {emp.status !== 'active' && canArchive && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-[#059669]"
                          title="Reativar funcionário"
                          onClick={() => handleReactivate(emp)}
                          data-testid={`reactivate-employee-${emp.employee_id}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      )}
                      {emp.status !== 'active' && canArchive && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-[#DC2626]"
                          onClick={() => { setSelectedEmployee(emp); setDeleteDialogOpen(true); }}
                          data-testid={`delete-employee-${emp.employee_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );})
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Dialog (uses refactored component with new fields) */}
      <EmployeeFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingEmployee={editingEmployee}
        formData={formData}
        setFormData={setFormData}
        locations={locations}
        onSubmit={handleSubmit}
      />

      {/* Archive Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Arquivar Funcionário</AlertDialogTitle>
            <AlertDialogDescription>
              Escolha o status para {selectedEmployee?.name}:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-4 py-4">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => handleArchive('inactive')}
              data-testid="archive-inactive-btn"
            >
              <UserX className="w-4 h-4 mr-2" />
              Marcar como Inativo
            </Button>
            <Button 
              variant="destructive" 
              className="flex-1"
              onClick={() => handleArchive('terminated')}
              data-testid="archive-terminated-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Marcar como Demitido
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Funcionário Permanentemente</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Digite a senha de exclusão para confirmar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label>Senha de Exclusão</Label>
            <Input
              data-testid="delete-password-input"
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Digite a senha"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletePassword('')}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-[#DC2626] hover:bg-[#DC2626]/90"
              data-testid="confirm-delete-btn"
            >
              Excluir Permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Face Registration Dialog */}
      <Dialog open={faceDialogOpen} onOpenChange={setFaceDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Registrar Biometria Facial</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-[#6B7280] mb-4">
              Funcionário: <strong>{selectedEmployee?.name}</strong>
            </p>
            <div className="bg-[#111111] aspect-video relative">
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                className="w-full h-full object-cover"
              />
              {capturingFace && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="text-white text-center">
                    <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    <p className="text-sm uppercase tracking-widest">Capturando...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFaceDialogOpen(false)}>Cancelar</Button>
            <Button onClick={captureFace} disabled={capturingFace} data-testid="capture-face-btn">
              <Camera className="w-4 h-4 mr-2" />
              Capturar Face
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
